import { useMemo } from "react";

import { Stack, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { formatShortDate, formatShortWeekdayDate, formatTime } from "@shared";

import { AppointmentPurposeChip } from "@/common/components/appointments/shared/components/appointment-purpose-chip/AppointmentPurposeChip";
import { ColumnFilterHeader, ColumnFilterOption } from "@/common/components/table/ColumnFilterHeader";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { composeDetails } from "@/pages/takeover/Takeover.utils";
import { useTranslation } from "@/translations";

import { TakeoverHistoryRow } from "../TakeoverHistoryPanel.types";

interface Params {
  serviceOptions: ColumnFilterOption[];
  serviceFilter: string | null;
  onServiceFilterChange: (value: string | null) => void;
}

const PLACEHOLDER = "—";

/** Column definitions for the takeover history table: timestamp, appointment, service (with a filter
 *  dropdown), details, given-up-by and taken-over-by. Read-only — no action column. */
export function useTakeoverHistoryColumns({
  serviceOptions,
  serviceFilter,
  onServiceFilterChange,
}: Params): Array<GridColDef<TakeoverHistoryRow>> {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const labels = useAppointmentLabels();

  return useMemo<Array<GridColDef<TakeoverHistoryRow>>>(
    () => [
      {
        field: "takeoverTimestampUtc",
        headerName: t("takeover.history.columns.timestamp"),
        type: "dateTime",
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<TakeoverHistoryRow>) => (
          <Stack justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="body2">
              {formatShortDate(params.row.takeoverTimestampUtc, locales[language])}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTime(params.row.takeoverTimestampUtc, locales[language])} {t("common.clock")}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "appointmentStartTimeUtc",
        headerName: t("takeover.history.columns.appointment"),
        type: "dateTime",
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<TakeoverHistoryRow>) => (
          <Stack justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="body2" fontWeight={600}>
              {formatShortWeekdayDate(params.row.appointmentStartTimeUtc, locales[language])}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.isAllDay
                ? t("takeover.allDay")
                : `${formatTime(params.row.appointmentStartTimeUtc, locales[language])} ${t("common.clock")}`}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "purposeKey",
        headerName: t("takeover.columns.service"),
        flex: 1,
        maxWidth: 180,
        renderHeader: () => (
          <ColumnFilterHeader
            label={t("takeover.columns.service")}
            options={serviceOptions}
            selectedValue={serviceFilter}
            onChange={onServiceFilterChange}
          />
        ),
        renderCell: (params: GridRenderCellParams<TakeoverHistoryRow>) => (
          <AppointmentPurposeChip label={labels.purposeLabelOf(params.row.purposeKey)} />
        ),
      },
      {
        field: "details",
        headerName: t("takeover.columns.details"),
        flex: 2,
        minWidth: 200,
        sortable: false,
        renderCell: (params: GridRenderCellParams<TakeoverHistoryRow>) => composeDetails(params.row, labels),
      },
      {
        field: "givenUpByUserCode",
        headerName: t("takeover.history.columns.givenUpBy"),
        flex: 1,
        maxWidth: 150,
        sortable: false,
        renderCell: (params: GridRenderCellParams<TakeoverHistoryRow>) => params.row.givenUpByUserCode ?? PLACEHOLDER,
      },
      {
        field: "takenOverByUserCode",
        headerName: t("takeover.columns.takenOverBy"),
        flex: 1,
        maxWidth: 150,
        sortable: false,
        renderCell: (params: GridRenderCellParams<TakeoverHistoryRow>) => params.row.takenOverByUserCode ?? PLACEHOLDER,
      },
    ],
    [t, language, labels, serviceOptions, serviceFilter, onServiceFilterChange]
  );
}
